/**
 * Sidebar accordion list for circuit markers (Figma modal pattern).
 */

/**
 * @typedef {object} MarkersListOptions
 * @property {HTMLElement | null} listEl
 * @property {HTMLElement | null} emptyEl
 * @property {HTMLElement | null} countEl
 * @property {() => { marker: import('three').Object3D; t: number }[]} getOrderedMarkers
 * @property {(key: string) => string} getCornerName
 * @property {(key: string) => { driverId: string | null; description: string }} getCornerMeta
 * @property {() => { id: string; firstName: string; lastName: string }[]} listDrivers
 * @property {(marker: import('three').Object3D) => void} onSelectMarker
 * @property {() => void} onDeselect
 * @property {(key: string, name: string, marker: import('three').Object3D) => void} onRename
 * @property {(key: string, driverId: string, marker: import('three').Object3D) => void} onAssignDriver
 * @property {(key: string, text: string, marker: import('three').Object3D) => void} onAssignDescription
 * @property {(marker: import('three').Object3D) => void} onDelete
 */

/**
 * @param {MarkersListOptions} options
 */
export function createMarkersList(options) {
  const {
    listEl,
    emptyEl,
    countEl,
    getOrderedMarkers,
    getCornerName,
    getCornerMeta,
    listDrivers,
    onSelectMarker,
    onDeselect,
    onRename,
    onAssignDriver,
    onAssignDescription,
    onDelete,
  } = options;

  /** @type {string | null} */
  let expandedKey = null;

  function syncExpandedState({ scroll = false } = {}) {
    if (!listEl) return;
    for (const item of listEl.querySelectorAll('.marker-item')) {
      const itemKey = item.getAttribute('data-corner-key');
      const isExpanded = itemKey === expandedKey;
      item.classList.toggle('is-expanded', isExpanded);
      const header = item.querySelector('.marker-item__header');
      if (header instanceof HTMLElement) {
        header.setAttribute('aria-expanded', String(isExpanded));
      }
      const body = item.querySelector('.marker-item__body');
      if (body instanceof HTMLElement) {
        body.toggleAttribute('inert', !isExpanded);
        body.setAttribute('aria-hidden', String(!isExpanded));
      }
    }
    if (scroll && expandedKey) scrollToKey(expandedKey);
  }

  function buildDriverOptions(select, selectedId) {
    select.replaceChildren();
    const none = document.createElement('option');
    none.value = '';
    none.textContent = 'Select driver…';
    select.appendChild(none);
    for (const driver of listDrivers()) {
      const opt = document.createElement('option');
      opt.value = driver.id;
      opt.textContent = `${driver.firstName} ${driver.lastName}`;
      select.appendChild(opt);
    }
    if ([...select.options].some((o) => o.value === selectedId)) {
      select.value = selectedId ?? '';
    }
  }

  /**
   * @param {string} key
   * @param {import('three').Object3D} marker
   */
  function createMarkerItem(key, marker) {
    const name = getCornerName(key);
    const { driverId, description } = getCornerMeta(key);
    const isExpanded = expandedKey === key;

    const item = document.createElement('div');
    item.className = `marker-item${isExpanded ? ' is-expanded' : ''}`;
    item.dataset.cornerKey = key;

    const header = document.createElement('button');
    header.type = 'button';
    header.className = 'marker-item__header';
    header.setAttribute('aria-expanded', String(isExpanded));

    const title = document.createElement('span');
    title.className = 'marker-item__title';
    title.textContent = name;

    const chevronWrap = document.createElement('span');
    chevronWrap.className = 'marker-item__chevron-wrap';
    chevronWrap.setAttribute('aria-hidden', 'true');
    chevronWrap.innerHTML =
      '<span class="marker-item__chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg></span>';

    header.append(title, chevronWrap);

    header.addEventListener('click', () => {
      if (expandedKey === key) {
        expandedKey = null;
        onDeselect();
        syncExpandedState();
        return;
      }
      expandedKey = key;
      onSelectMarker(marker);
      syncExpandedState({ scroll: true });
    });

    const body = document.createElement('div');
    body.className = 'marker-item__body';
    body.setAttribute('aria-hidden', String(!isExpanded));
    if (!isExpanded) body.setAttribute('inert', '');

    const bodyInner = document.createElement('div');
    bodyInner.className = 'marker-item__body-inner';

    const labelField = document.createElement('div');
    labelField.className = 'marker-item__field';
    const labelLabel = document.createElement('span');
    labelLabel.className = 'marker-item__field-label';
    labelLabel.textContent = 'Marker label';
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.className = 'corner-name-input marker-item__input';
    nameInput.maxLength = 32;
    nameInput.placeholder = 'Marker name…';
    nameInput.value = name;
    nameInput.addEventListener('input', () => {
      onRename(key, nameInput.value, marker);
      title.textContent = nameInput.value.trim() || 'Marker name';
    });
    nameInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') nameInput.blur();
    });
    labelField.append(labelLabel, nameInput);

    const driverField = document.createElement('div');
    driverField.className = 'marker-item__field';
    const driverLabel = document.createElement('span');
    driverLabel.className = 'marker-item__field-label';
    driverLabel.textContent = 'Driver';
    const driverSelectWrap = document.createElement('div');
    driverSelectWrap.className = 'marker-item__select-wrap';
    const driverSelect = document.createElement('select');
    driverSelect.className = 'corner-driver-select marker-item__select';
    buildDriverOptions(driverSelect, driverId ?? '');
    driverSelect.addEventListener('change', () => {
      onAssignDriver(key, driverSelect.value, marker);
    });
    const driverChevron = document.createElement('span');
    driverChevron.className = 'marker-item__select-chevron';
    driverChevron.setAttribute('aria-hidden', 'true');
    driverChevron.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>';
    driverSelectWrap.append(driverSelect, driverChevron);
    driverField.append(driverLabel, driverSelectWrap);

    const quoteField = document.createElement('div');
    quoteField.className = 'marker-item__field marker-item__field--quote';
    const quoteLabel = document.createElement('span');
    quoteLabel.className = 'marker-item__field-label';
    quoteLabel.textContent = 'Driver quote';
    const quoteInput = document.createElement('textarea');
    quoteInput.className = 'corner-description-input marker-item__textarea';
    quoteInput.rows = 4;
    quoteInput.maxLength = 500;
    quoteInput.placeholder = 'Marker quote…';
    quoteInput.value = description;
    quoteInput.addEventListener('input', () => {
      onAssignDescription(key, quoteInput.value, marker);
    });
    quoteField.append(quoteLabel, quoteInput);

    const footer = document.createElement('div');
    footer.className = 'marker-item__footer';
    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'modal-btn modal-btn--text modal-btn--danger marker-item__delete';
    deleteBtn.textContent = 'Delete';
    deleteBtn.addEventListener('click', () => onDelete(marker));
    footer.append(deleteBtn);

    bodyInner.append(labelField, driverField, quoteField, footer);
    body.append(bodyInner);
    item.append(header, body);
    return item;
  }

  function render() {
    const ordered = getOrderedMarkers();
    const count = ordered.length;

    if (countEl) countEl.textContent = String(count);
    if (emptyEl) emptyEl.hidden = count > 0;
    if (listEl) {
      listEl.hidden = count === 0;
      listEl.replaceChildren();
      for (const { marker } of ordered) {
        const key = marker.userData.cornerKey;
        if (typeof key !== 'string') continue;
        listEl.appendChild(createMarkerItem(key, marker));
      }
    }

    if (expandedKey && !ordered.some((e) => e.marker.userData.cornerKey === expandedKey)) {
      expandedKey = null;
    }
  }

  /**
   * @param {string | null} key
   */
  function setExpanded(key) {
    expandedKey = key;
    if (listEl?.querySelector('.marker-item')) {
      syncExpandedState({ scroll: Boolean(key) });
      return;
    }
    render();
    if (key) scrollToKey(key);
  }

  function collapse() {
    expandedKey = null;
    if (listEl?.querySelector('.marker-item')) {
      syncExpandedState();
      return;
    }
    render();
  }

  /**
   * @returns {string | null}
   */
  function getExpandedKey() {
    return expandedKey;
  }

  /**
   * @param {string} key
   */
  function scrollToKey(key) {
    if (!listEl) return;
    const item = listEl.querySelector(`[data-corner-key="${key}"]`);
    item?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  function refreshDrivers() {
    if (!listEl) return;
    for (const select of listEl.querySelectorAll('.marker-item__select')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const item = select.closest('[data-corner-key]');
      const key = item?.getAttribute('data-corner-key');
      if (!key) continue;
      const { driverId } = getCornerMeta(key);
      buildDriverOptions(select, driverId ?? '');
    }
  }

  return {
    render,
    setExpanded,
    collapse,
    getExpandedKey,
    scrollToKey,
    refreshDrivers,
  };
}
